import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerFooter, DrawerDescription } from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Eye } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useNavigate } from "react-router-dom";

interface Props {
  open: boolean;
  noDocumento: string;
  existingCount: number;
  existingIds?: string[];
  onCancel: () => void;
  onReplace: () => void;
  replacing: boolean;
}

export function DuplicateDocumentModal({ open, noDocumento, existingCount, existingIds, onCancel, onReplace, replacing }: Props) {
  const isMobile = useIsMobile();
  const navigate = useNavigate();

  const handleViewExisting = () => {
    onCancel();
    const targetId = existingIds?.[0];
    if (targetId) {
      navigate(`/dashboard/factura-internacional?docId=${targetId}`);
    }
  };

  const content = (
    <div className="flex flex-col items-center gap-4 py-4">
      <div className="h-12 w-12 rounded-full bg-amber-500/10 flex items-center justify-center">
        <AlertTriangle className="h-6 w-6 text-amber-500" />
      </div>
      <div className="text-sm text-muted-foreground text-center max-w-sm space-y-1">
        <p>
          El documento <strong className="text-foreground">{noDocumento}</strong> ya se encuentra registrado y activo en el sistema
          {existingCount > 1 ? ` (${existingCount} registros)` : ""}.
        </p>
        <p className="text-xs">
          Verifica si necesitas actualizarlo o si es un error de carga.
        </p>
      </div>
    </div>
  );

  const actions = (
    <div className="flex flex-wrap items-center gap-2 w-full justify-end">
      {existingIds && existingIds.length > 0 && (
        <Button variant="outline" onClick={handleViewExisting} disabled={replacing} className="mr-auto">
          <Eye className="h-4 w-4 mr-1.5" />
          Ver existente
        </Button>
      )}
      <Button variant="ghost" onClick={onCancel} disabled={replacing} className="text-muted-foreground">
        Cancelar
      </Button>
      <Button
        variant="default"
        onClick={onReplace}
        disabled={replacing}
        className="bg-amber-500 text-white hover:bg-amber-600"
      >
        {replacing ? "Reemplazando..." : "Reemplazar"}
      </Button>
    </div>
  );

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={(o) => !o && onCancel()}>
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Documento Duplicado
            </DrawerTitle>
            <DrawerDescription>Selecciona una acción para continuar</DrawerDescription>
          </DrawerHeader>
          <div className="px-4">{content}</div>
          <DrawerFooter>
            {actions}
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="glass border-border sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-foreground flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            Documento Duplicado
          </DialogTitle>
          <DialogDescription>Selecciona una acción para continuar</DialogDescription>
        </DialogHeader>
        {content}
        <DialogFooter>{actions}</DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
